import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Loader2, Globe, KeyRound, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import api from '../api';
import { useLanguage } from '../context/LanguageContext';
import BrandLogo from '../components/BrandLogo';

export default function ForgotPasswordPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    
    setErrorMsg('');
    setIsLoading(true);
    
    try {
      await api.post('/auth/forgot-password', { email });
      setSuccessMsg(t('auth.codeSentMsg'));
      setStep(2);
    } catch (error: any) {
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          const detail = error.response.data.detail.toLowerCase();
          if (detail.includes('google')) {
            setErrorMsg(t('errors.google_user_password_reset'));
          } else if (detail.includes('not found')) {
            setErrorMsg(t('errors.user_not_found'));
          } else {
            setErrorMsg(error.response.data.detail);
          }
        } else if (Array.isArray(error.response.data.detail)) {
          setErrorMsg(error.response.data.detail[0].msg);
        }
      } else {
        setErrorMsg(t('errors.network'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !code || !newPassword) return;
    
    if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/\d/.test(newPassword)) {
      setErrorMsg(t('auth.passwordRequirements'));
      return;
    }

    setErrorMsg('');
    setSuccessMsg('');
    setIsLoading(true);
    
    try {
      await api.post('/auth/reset-password', {
        email,
        code,
        new_password: newPassword
      });
      setSuccessMsg(t('auth.passwordResetMsg'));
      setTimeout(() => {
        navigate('/login');
      }, 700);
    } catch (error: any) {
      setIsLoading(false);
      if (error.response?.status === 422) {
        const detail = error.response?.data?.detail;
        if (Array.isArray(detail)) {
          setErrorMsg(detail[0].msg);
        } else {
          setErrorMsg(t('auth.passwordRequirements'));
        }
      } else if (error.response?.status === 400 && error.response?.data?.detail === "Invalid verification code") {
        setErrorMsg(t('errors.invalid_code'));
      } else if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          const detail = error.response.data.detail.toLowerCase();
          if (detail.includes('google')) {
            setErrorMsg(t('errors.google_user_password_reset'));
          } else if (detail.includes('not found')) {
            setErrorMsg(t('errors.user_not_found'));
          } else {
            setErrorMsg(error.response.data.detail);
          }
        } else if (Array.isArray(error.response.data.detail)) {
          setErrorMsg(error.response.data.detail[0].msg);
        }
      } else {
        setErrorMsg(t('errors.network'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-goszakup-600/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Language Toggle */}
      <div className="absolute top-6 right-6 lg:top-8 lg:right-8 z-20">
        <button 
          onClick={toggleLanguage} 
          type="button"
          className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm text-gray-700 border border-gray-200 rounded-xl hover:bg-white hover:text-goszakup-600 hover:border-goszakup-300 transition-all shadow-sm font-medium"
        >
          <Globe className="w-4 h-4" />
          <span>{language === 'ru' ? 'РУ' : 'ҚАЗ'}</span>
        </button>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center pt-6">
          <Link to="/" className="flex items-center gap-3 text-goszakup-600 hover:text-goszakup-700 transition-colors group">
            <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 group-hover:shadow-md transition-all">
              <BrandLogo variant="full" className="h-24 w-auto" />
            </div>
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {step === 1 ? t('auth.forgotPasswordTitle') : t('auth.resetPasswordTitle')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 px-4">
          {step === 1 ? t('auth.forgotPasswordDesc') : t('auth.resetPasswordDesc')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-3xl sm:px-10 border border-gray-100">
          {successMsg && (
            <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-xl flex flex-col items-center gap-2 text-sm border border-green-200 text-center">
              <span>{successMsg}</span>
              {step === 2 && <Loader2 className="w-4 h-4 animate-spin text-green-700" />}
            </div>
          )}
          {errorMsg && (
            <div className="mb-4 bg-red-50 text-red-600 p-3 rounded-xl flex items-center gap-2 text-sm border border-red-100">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          
          {step === 1 ? (
            <form className="space-y-6" onSubmit={handleRequestCode}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('auth.emailLabel')}
                </label>
                <div className="mt-1 relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                    placeholder="name@example.com"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-goszakup-600 hover:bg-goszakup-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-goszakup-500 transition-all duration-300 hover:scale-[1.02] disabled:opacity-75 disabled:hover:scale-100 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('auth.sendingCode')}
                    </>
                  ) : (
                    t('auth.sendCodeBtn')
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form className="space-y-6" onSubmit={handleResetPassword}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.emailLabel')}
                </label>
                <div className="px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500">
                  {email}
                </div>
              </div>

              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  {t('auth.codeLabel')}
                </label>
                <div className="mt-1 relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyRound className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all text-center tracking-widest font-mono text-lg"
                    placeholder="123456"
                    maxLength={6}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                  {t('auth.newPasswordLabel')}
                </label>
                <div className="mt-1 relative rounded-xl shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showNewPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:border-transparent sm:text-sm transition-all"
                    placeholder="••••••••"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="text-gray-400 hover:text-gray-500 focus:outline-none"
                      tabIndex={-1}
                    >
                      {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                {newPassword && (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/\d/.test(newPassword)) && (
                  <p className="mt-1 text-xs text-red-500">{t('auth.passwordRequirements')}</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-goszakup-600 hover:bg-goszakup-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-goszakup-500 transition-all duration-300 hover:scale-[1.02] disabled:opacity-75 disabled:hover:scale-100 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('auth.resetting')}
                    </>
                  ) : (
                    t('auth.resetBtn')
                  )}
                </button>
              </div>
            </form>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">
                  <ArrowLeft className="w-4 h-4 inline-block mr-1" />
                </span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link to="/login" className="font-medium text-goszakup-600 hover:text-goszakup-500 transition-colors">
                {t('auth.backToLogin')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
